import { useMemo }                      from 'react';
import { RUNE_WORDS, getActiveRuneWords } from '../../data/runewords';
import { RUNE_CATALOGUE, RARITY_COLORS, formatStatMods } from '../../data/items';

// Build a name → rarity map from the catalogue once (module-level, not per render)
const RUNE_RARITY = Object.fromEntries(RUNE_CATALOGUE.map(r => [r.name, r.rarity]));

/**
 * Full-screen modal listing all 20 Rune Words, their required runes, and bonuses.
 * Active words (all required runes currently socketed) are highlighted.
 *
 * Props:
 *  equipped         — current equipped map (to determine which words are active)
 *  inventory        — player's inventory array (to check which runes they own)
 *  onEquipRuneWord  — callback(runeWord) to socket all available required runes
 *  onClose()        — close callback
 */
export function RuneWordModal({ equipped, inventory = [], onEquipRuneWord, onClose }) {
  const activeIds = useMemo(() => {
    const active = getActiveRuneWords(equipped);
    return new Set(active.map(w => w.id));
  }, [equipped]);

  // Set of rune names the player currently has in inventory (not socketed)
  const inventoryRuneNames = useMemo(() => {
    return new Set(inventory.filter(item => item?.slotType === 'rune').map(item => item.name));
  }, [inventory]);

  // Set of rune names already socketed
  const socketedRuneNames = useMemo(() => {
    return new Set(Object.values(equipped).filter(item => item?.slotType === 'rune').map(item => item.name));
  }, [equipped]);

  // Group words by required rune count for section headers
  const groups = useMemo(() => {
    const map = {};
    for (const word of RUNE_WORDS) {
      const n = word.requiredRunes.length;
      (map[n] ??= []).push(word);
    }
    return Object.entries(map).map(([n, words]) => ({ count: Number(n), words }));
  }, []);

  return (
    <div className="rw-overlay" onClick={onClose}>
      <div className="rw-modal" onClick={e => e.stopPropagation()}>
        <div className="rw-header">
          <h2 className="rw-title">⬡ Rune Words</h2>
          <p className="rw-subtitle">
            Socket all required runes simultaneously to unlock the set bonus.
          </p>
          <button className="rw-close" onClick={onClose}>✕</button>
        </div>

        <div className="rw-body">
          {groups.map(({ count, words }) => (
            <section key={count} className="rw-group">
              <h3 className="rw-group-title">
                {count}-Rune Words
                <span className="rw-group-badge">{words.length}</span>
              </h3>
              <div className="rw-cards">
                {words.map(word => {
                  const active     = activeIds.has(word.id);
                  const bonusLines = formatStatMods(word.statMods);
                  // Count how many required runes the player can still equip
                  const equippable = word.requiredRunes.filter(
                    n => !socketedRuneNames.has(n) && inventoryRuneNames.has(n)
                  );
                  const missing = word.requiredRunes.filter(
                    n => !socketedRuneNames.has(n) && !inventoryRuneNames.has(n)
                  );
                  return (
                    <div key={word.id} className={`rw-card${active ? ' rw-card--active' : ''}`}>
                      {/* Card header */}
                      <div className="rw-card-header">
                        <span className="rw-card-emoji">{word.emoji}</span>
                        <span className="rw-card-name">{word.name}</span>
                        {active && <span className="rw-card-active-badge">✦ ACTIVE</span>}
                        {!active && equippable.length > 0 && (
                          <button
                            className="rw-equip-btn"
                            onClick={() => onEquipRuneWord?.(word)}
                            title={`Socket ${equippable.length} rune${equippable.length !== 1 ? 's' : ''} from inventory`}
                          >
                            + Equip {equippable.length}/{word.requiredRunes.length}
                          </button>
                        )}
                      </div>

                      {/* Flavour text */}
                      <p className="rw-card-desc">{word.description}</p>

                      {/* Required runes — colour-coded by availability */}
                      <div className="rw-card-reqs">
                        {word.requiredRunes.map(runeName => {
                          const rarity   = RUNE_RARITY[runeName] ?? 'common';
                          const color    = RARITY_COLORS[rarity] ?? '#9e9e9e';
                          const socketed = socketedRuneNames.has(runeName);
                          const owned    = inventoryRuneNames.has(runeName);
                          const extraCls = socketed ? ' rw-rune-tag--socketed'
                                         : owned    ? ' rw-rune-tag--owned'
                                         : ' rw-rune-tag--missing';
                          return (
                            <span
                              key={runeName}
                              className={`rw-rune-tag${extraCls}`}
                              style={{ '--rune-color': color }}
                              title={socketed ? 'Socketed ✓' : owned ? 'In inventory' : 'Not owned'}
                            >
                              {socketed ? '✓' : owned ? '🔮' : '✕'} {runeName}
                            </span>
                          );
                        })}
                      </div>

                      {/* Missing rune summary */}
                      {!active && missing.length > 0 && (
                        <p className="rw-missing-note">
                          {missing.length} rune{missing.length !== 1 ? 's' : ''} not in inventory
                        </p>
                      )}

                      {/* Bonus effects */}
                      <div className="rw-card-bonuses">
                        {bonusLines.map(line => (
                          <span key={line} className="rw-bonus-line">✦ {line}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
